import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { convertToPersianNumber } from 'utils/PersianNumber';

const HeroSection = () => {
  const heroRef = useRef(null);
  const calendarRef = useRef(null);
  const notificationRef = useRef(null);
  const counterRef = useRef(null);
  const [appointmentsCount, setAppointmentsCount] = useState(0);
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    // Check for saved language preference
    const savedLanguage = localStorage.getItem('language') || 'en';
    setLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    const ctx = gsap?.context(() => {
      // Hero entrance animation
      gsap?.fromTo(
        heroRef?.current,
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, duration: 1, ease: 'power2.out' }
      );

      // Floating calendar animation
      gsap?.to(calendarRef?.current, {
        y: -20,
        duration: 3,
        repeat: -1,
        yoyo: true,
        ease: 'power2.inOut',
      });

      // Notification popup animation
      gsap?.fromTo(
        notificationRef?.current,
        { scale: 0, opacity: 0 },
        {
          scale: 1,
          opacity: 1,
          duration: 0.8,
          delay: 1.5,
          ease: 'back.out(1.7)',
        }
      );

      // Counter animation
      gsap?.to(
        {},
        {
          duration: 2,
          delay: 2,
          onUpdate: function () {
            const progress = this.progress();
            setAppointmentsCount(Math.floor(progress * 15847));
          },
        }
      );
    }, heroRef);

    return () => ctx?.revert();
  }, []);

  const content = {
    en: {
      headline: 'Turn Booking Chaos Into Seamless Customer Experiences',
      subheadline:
        'The only booking system that automatically manages appointments across social media and web while using AI to reduce no-shows and coordinate staff.',
      primaryCta: 'Book Demo',
      secondaryCta: 'Contact Sales',
      trustMetrics: {
        businesses: '2,847+ Businesses Served',
        appointments: 'Appointments Managed Today',
        reduction: '85% Average No-Show Reduction',
      },
    },
    fa: {
      headline: 'تبدیل فرآیندهای آشفته رزرو به تجربه‌ای روان و بی‌دردسر',
      subheadline:
        'تنها سیستم رزرو که به طور خودکار قرارملاقات‌ها را در شبکه‌های اجتماعی و وب مدیریت می‌کند و از هوش مصنوعی برای کاهش غیبت و هماهنگی کارکنان استفاده می‌کند.',
      primaryCta: 'درخواست دمو',
      secondaryCta: 'تماس با فروش',
      trustMetrics: {
        businesses: '۲۵+ کسب‌وکار خدمات‌رسانی',
        appointments: 'قرارملاقات مدیریت شده امروز',
        reduction: '۸۵% کاهش متوسط عدم حضور',
      },
    },
  };

  const currentContent = content?.[language];

  return (
    <section
      ref={heroRef}
      id="hero"
      className="relative min-h-screen bg-gradient-to-br from-blue-50 via-white to-emerald-50 overflow-hidden pt-16">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      <div className="container mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-screen py-20">
          {/* Content Side */}
          <div
            className={`space-y-8 ${
              language === 'fa' ? 'text-right' : 'text-left'
            }`}>
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                {currentContent?.headline}
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-2xl">
                {currentContent?.subheadline}
              </p>
            </div>

            {/* CTA Buttons */}
            <div
              className={`flex flex-col sm:flex-row gap-4 ${
                language === 'fa' ? 'sm:justify-end' : 'sm:justify-start'
              }`}>
              <Button
                variant="default"
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                iconName="Calendar"
                iconPosition="left">
                {currentContent?.primaryCta}
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-4 text-lg font-semibold"
                iconName="Phone"
                iconPosition="left">
                {currentContent?.secondaryCta}
              </Button>
            </div>

            {/* Trust Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-8">
              <div
                className={`text-center md:${
                  language === 'fa' ? 'text-right' : 'text-left'
                }`}>
                <div className="text-2xl font-bold text-blue-600">۲۵+</div>
                <div className="text-sm text-gray-600">
                  {currentContent?.trustMetrics?.businesses}
                </div>
              </div>
              <div
                className={`text-center md:${
                  language === 'fa' ? 'text-right' : 'text-left'
                }`}>
                <div
                  ref={counterRef}
                  className="text-2xl font-bold text-emerald-600">
                  {appointmentsCount?.toLocaleString()}
                </div>
                <div className="text-sm text-gray-600">
                  {currentContent?.trustMetrics?.appointments}
                </div>
              </div>
              <div
                className={`text-center md:${
                  language === 'fa' ? 'text-right' : 'text-left'
                }`}>
                <div className="text-2xl font-bold text-green-600"> ۸۵%</div>
                <div className="text-sm text-gray-600">
                  {currentContent?.trustMetrics?.reduction}
                </div>
              </div>
            </div>
          </div>

          {/* Visual Side */}
          <div className="relative">
            {/* Main Calendar Visual */}
            <div ref={calendarRef} className="relative z-20">
              <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md mx-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    آبان ۱۴۰۴
                  </h3>
                  <div className="flex space-x-2">
                    <button className="p-1 hover:bg-gray-100 rounded">
                      <Icon name="ChevronLeft" size={20} />
                    </button>
                    <button className="p-1 hover:bg-gray-100 rounded">
                      <Icon name="ChevronRight" size={20} />
                    </button>
                  </div>
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1 text-center text-sm">
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S']?.map((day, index) => (
                    <div key={index} className="p-2 text-gray-500 font-medium">
                      {convertToPersianNumber(day)}
                    </div>
                  ))}
                  {Array.from({ length: 35 }, (_, i) => {
                    const day = i - 2;
                    const isToday = day === 11;
                    const hasBooking = [5, 8, 11, 15, 18, 22, 25]?.includes(
                      day
                    );

                    return (
                      <div
                        key={i}
                        className={`p-2 text-sm relative ${
                          day < 1 || day > 30
                            ? 'text-gray-300'
                            : isToday
                            ? 'bg-blue-600 text-white rounded-full font-semibold'
                            : hasBooking
                            ? 'bg-emerald-100 text-emerald-700 rounded font-medium'
                            : 'text-gray-700 hover:bg-gray-100 rounded'
                        }`}>
                        {day > 0 && day <= 30 ? day : ''}
                        {hasBooking && day !== 11 && (
                          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-emerald-500 rounded-full"></div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Floating Notification */}
            <div
              ref={notificationRef}
              className="absolute top-4 right-4 bg-white rounded-lg shadow-lg p-4 max-w-xs z-30">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <Icon name="Check" size={16} color="#059669" />
                </div>
                <div>
                  <p dir="rtl" className="text-sm font-medium text-gray-900">
                    رزرو شما انجام شد!
                  </p>
                  <p className="text-xs text-gray-600">سارا - Nail Artist</p>
                  <p className="text-xs text-gray-500">آبان ۱۲, ۲:۳۰ عصر</p>
                </div>
              </div>
            </div>

            {/* Background Decorative Elements */}
            <div className="absolute -top-10 -left-10 w-20 h-20 bg-blue-200 rounded-full opacity-60 animate-pulse"></div>
            <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-emerald-200 rounded-full opacity-40 animate-pulse delay-1000"></div>
            <div className="absolute top-1/2 -left-20 w-16 h-16 bg-purple-200 rounded-full opacity-50 animate-pulse delay-500"></div>
          </div>
        </div>
      </div>
      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <Icon name="ChevronDown" size={24} color="#6B7280" />
      </div>
    </section>
  );
};

export default HeroSection;
